import React, { useState } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQueries } from '@tanstack/react-query';
import i18n from '../../../i18n';
import BASE_URL from '../../../config/config';
import { cabecerasAuth, claves } from '../../../queries/cliente';
import './operaciones.css';

// Estados que cuentan como "abierto" en este panel
const ABIERTOS = ['abierto', 'retenido'];
const HORA = 60 * 60 * 1000;
const MAX_ATENCION = 6;
const MAX_RECIENTES = 6;

const departmentName = (department) => (
  i18n.t(`dashboardStaff.departamentos.${department}`, { defaultValue: null })
  || String(department || i18n.t('dashboardStaff.sinAsignar')).replace(/[_-]/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase())
);

const formatDuration = (seconds) => {
  if (!seconds) return i18n.t('dashboardStaff.sinDatos');
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const d = i18n.t('dashboardStaff.dias');
  const h = i18n.t('dashboardStaff.horas');
  const m = i18n.t('dashboardStaff.minutos');
  if (days) return `${days} ${d} ${hours} ${h}`;
  if (hours) return `${hours} ${h} ${minutes} ${m}`;
  return `${Math.max(minutes, 1)} ${m}`;
};

// Espera en una sola unidad: en una lista se compara de un vistazo
const esperaCorta = (desde) => {
  const minutos = Math.max(1, Math.floor((Date.now() - new Date(desde).getTime()) / 60000));
  if (minutos < 60) return `${minutos} ${i18n.t('dashboardStaff.minutos')}`;
  const horas = Math.floor(minutos / 60);
  if (horas < 24) return `${horas} ${i18n.t('dashboardStaff.horas')}`;
  return `${Math.floor(horas / 24)} ${i18n.t('dashboardStaff.dias')}`;
};

const formatUpdatedAt = (dateValue) => {
  const date = new Date(dateValue);
  const elapsed = Date.now() - date.getTime();
  const minutes = Math.max(0, Math.floor(elapsed / 60000));
  if (minutes < 1) return i18n.t('dashboardStaff.ahoraMismo');
  if (minutes < 60) return i18n.t('dashboardStaff.hace', { tiempo: `${minutes} ${i18n.t('dashboardStaff.minutos')}` });
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return i18n.t('dashboardStaff.hace', { tiempo: `${hours} ${i18n.t('dashboardStaff.horas')}` });
  return date.toLocaleDateString(i18n.language, { day: 'numeric', month: 'short' });
};

const statusLabel = (status) => i18n.t(`dashboardStaff.estados.${status}`, { defaultValue: status });

const horasDesde = (fecha) => (Date.now() - new Date(fecha).getTime()) / HORA;

/** "a, b y c" con la conjunción del idioma activo */
const unirLista = (partes) => {
  try {
    return new Intl.ListFormat(i18n.language, { style: 'long', type: 'conjunction' }).format(partes);
  } catch {
    return partes.join(', ');
  }
};

/**
 * Panel de operaciones para administradores y técnicos. Empieza por lo que
 * hay que hacer ahora (la lista de tickets que necesitan a alguien) y deja
 * las cifras de contexto a un lado. Cada atajo abre el gestor ya filtrado.
 */
const DashboardStaff = ({ onLinkClick }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [verDesglose, setVerDesglose] = useState(false);

  /**
   * Abre el gestor de tickets con un filtro ya aplicado. El gestor lee sus
   * filtros de localStorage al montar, así que basta con dejarlos escritos
   * (reemplazando los anteriores) antes de navegar.
   */
  const verTicketsCon = (filtros) => {
    try {
      localStorage.setItem('ticketManagerFiltros', JSON.stringify(filtros));
    } catch (err) {
      // Almacenamiento bloqueado: se abre el gestor sin filtrar
    }
    if (onLinkClick) onLinkClick();
    navigate('/dashboard/tickets');
  };

  // Tres consultas independientes y cacheadas: el gestor de tickets reutiliza
  // las dos primeras sin volver a pedirlas al servidor.
  const [consultaAbiertos, consultaArchivados, consultaStats] = useQueries({
    queries: [
      {
        queryKey: claves.ticketsTodos(),
        queryFn: async () => (await axios.get(`${BASE_URL}/tickets/total/?page=1&limit=1000`, cabecerasAuth())).data.tickets || [],
      },
      {
        queryKey: claves.ticketsArchivados(),
        queryFn: async () => (await axios.get(`${BASE_URL}/tickets/total-archivados/?page=1&limit=1000`, cabecerasAuth())).data.tickets || [],
      },
      {
        queryKey: claves.estadisticasTickets(),
        queryFn: async () => (await axios.get(`${BASE_URL}/api/admin/stats/tickets`, cabecerasAuth())).data,
      },
    ],
  });

  const consultas = [consultaAbiertos, consultaArchivados, consultaStats];

  if (consultas.some((c) => c.isError)) {
    return <p className="ops-estado">{t('dashboardStaff.errorCargar')}</p>;
  }
  if (consultas.some((c) => c.isPending)) {
    return <p className="ops-estado">{t('dashboardStaff.cargando')}</p>;
  }

  const tickets = [...(consultaAbiertos.data || []), ...(consultaArchivados.data || [])];
  const stats = consultaStats.data || {};

  const abiertos = tickets.filter((ticket) => ABIERTOS.includes(ticket.estado_del_ticket));
  const esperan = abiertos.filter((ticket) => ticket.estado_del_chat === 'sin_responder');
  const sinResponsable = abiertos.filter((ticket) => ticket.usuario_encargado === 'sin_asignar');
  const urgentes = abiertos.filter((ticket) => ticket.prioridad === 'urgente');
  const resueltos = tickets.filter((ticket) => ticket.estado_del_ticket === 'cerrado');

  // Qué necesita a alguien y por qué. La espera cuenta desde el último
  // movimiento si falta respuesta, y desde el alta si falta responsable.
  const atencion = abiertos
    .map((ticket) => {
      const motivos = [];
      if (ticket.prioridad === 'urgente') motivos.push('urgente');
      if (ticket.estado_del_chat === 'sin_responder') motivos.push('sinRespuesta');
      if (ticket.usuario_encargado === 'sin_asignar') motivos.push('sinResponsable');
      const desde = ticket.estado_del_chat === 'sin_responder' ? ticket.updatedAt : ticket.createdAt;
      return { ticket, motivos, desde };
    })
    .filter((fila) => fila.motivos.length)
    .sort((a, b) => (
      Number(b.motivos.includes('urgente')) - Number(a.motivos.includes('urgente'))
      || new Date(a.desde) - new Date(b.desde)
    ));

  // Antigüedad de lo abierto en tramos que no se solapan
  const tramos = [
    { clave: 'tramoDia', n: abiertos.filter((x) => horasDesde(x.createdAt) < 24).length },
    { clave: 'tramo3', n: abiertos.filter((x) => horasDesde(x.createdAt) >= 24 && horasDesde(x.createdAt) < 72).length },
    { clave: 'tramo7', n: abiertos.filter((x) => horasDesde(x.createdAt) >= 72 && horasDesde(x.createdAt) < 168).length },
    { clave: 'tramoMas', n: abiertos.filter((x) => horasDesde(x.createdAt) >= 168).length },
  ];

  const porDpto = Object.values(tickets.reduce((acc, ticket) => {
    const dpto = ticket.dpto || 'no_especificado';
    acc[dpto] = acc[dpto] || { dpto, total: 0, abiertos: 0 };
    acc[dpto].total += 1;
    if (ABIERTOS.includes(ticket.estado_del_ticket)) acc[dpto].abiertos += 1;
    return acc;
  }, {})).sort((a, b) => b.total - a.total);
  const maxDpto = porDpto[0]?.total || 1;

  const recientes = [...tickets].sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt)).slice(0, MAX_RECIENTES);
  const desgloseDpto = Object.entries(stats.avgResolutionByDept || {})
    .filter(([dpto]) => !dpto.startsWith('intervencion_'))
    .sort(([, a], [, b]) => b.count - a.count);

  // La frase de cabecera: el estado del soporte en una línea
  const partes = [
    esperan.length && t('dashboardStaff.ops.parteEsperan', { count: esperan.length }),
    urgentes.length && t('dashboardStaff.ops.parteUrgentes', { count: urgentes.length }),
    sinResponsable.length && t('dashboardStaff.ops.parteSinResponsable', { count: sinResponsable.length }),
  ].filter(Boolean);
  const resumen = abiertos.length === 0
    ? t('dashboardStaff.ops.resumenVacio')
    : partes.length
      ? `${t('dashboardStaff.ops.resumenAbiertos', { count: abiertos.length })}: ${unirLista(partes)}.`
      : `${t('dashboardStaff.ops.resumenAbiertos', { count: abiertos.length })} ${t('dashboardStaff.ops.resumenTodoBien')}`;

  const atajos = [
    { clave: 'atajoAbiertos', n: abiertos.length, filtros: { selectedEstados: ABIERTOS } },
    { clave: 'atajoEsperan', n: esperan.length, filtros: { selectedEstados: ABIERTOS, selectedChats: ['sin_responder'] }, tono: 'aviso' },
    { clave: 'atajoSinResponsable', n: sinResponsable.length, filtros: { selectedEstados: ABIERTOS, selectedTecnicos: ['sin_asignar'] }, tono: 'aviso' },
    { clave: 'atajoUrgentes', n: urgentes.length, filtros: { selectedEstados: ABIERTOS, selectedPrioridades: ['urgente'] }, tono: 'peligro' },
    { clave: 'atajoResueltos', n: resueltos.length, filtros: { selectedEstados: ['cerrado'] } },
    { clave: 'atajoTodos', n: tickets.length, filtros: {} },
  ];

  return (
    <div className="ops">
      <div className="ops-cabecera">
        <div>
          <h1>{t('dashboardStaff.ops.titulo')}</h1>
          <p>{resumen}</p>
        </div>
        <Link to="/dashboard/tickets" className="ops-boton" onClick={onLinkClick}>
          {t('dashboardStaff.ops.abrirGestor')}
        </Link>
      </div>

      <nav className="ops-atajos" aria-label={t('dashboardStaff.ops.atajosAria')}>
        {atajos.map(({ clave, n, filtros, tono }) => (
          <button
            key={clave}
            type="button"
            className={`ops-atajo${tono && n > 0 ? ` ops-atajo--${tono}` : ''}`}
            onClick={() => verTicketsCon(filtros)}
          >
            <strong>{n}</strong>
            <span>{t(`dashboardStaff.ops.${clave}`)}</span>
          </button>
        ))}
      </nav>

      <div className="ops-rejilla">
        <div className="ops-principal">
          <section className="ops-panel ops-atencion" aria-labelledby="ops-atencion-titulo">
            <div className="ops-panel-cabecera">
              <h2 id="ops-atencion-titulo">{t('dashboardStaff.ops.atencionTitulo')}</h2>
              {atencion.length > MAX_ATENCION && (
                <button type="button" className="ops-enlace" onClick={() => verTicketsCon({ selectedEstados: ABIERTOS })}>
                  {t('dashboardStaff.ops.atencionVerMas', { count: atencion.length })}
                </button>
              )}
            </div>

            {atencion.length === 0 ? (
              <p className="ops-vacio">{t('dashboardStaff.ops.atencionVacio')}</p>
            ) : (
              <ul className="ops-lista">
                {atencion.slice(0, MAX_ATENCION).map(({ ticket, motivos, desde }) => (
                  <li key={ticket.identificador}>
                    <Link to={`/ticket/${ticket.identificador}`} className="ops-fila" onClick={onLinkClick}>
                      <span className="ops-fila-texto">
                        <span className="ops-fila-asunto">{ticket.asunto}</span>
                        <span className="ops-fila-meta">
                          <span className="ops-id">#{ticket.identificador}</span>
                          {motivos.map((motivo) => (
                            <span key={motivo} className={`ops-motivo ops-motivo--${motivo}`}>
                              {t(`dashboardStaff.ops.motivo_${motivo}`)}
                            </span>
                          ))}
                        </span>
                      </span>
                      <span className="ops-espera" title={new Date(desde).toLocaleString(i18n.language)}>
                        <strong>{esperaCorta(desde)}</strong>
                        <span>{t('dashboardStaff.ops.esperando')}</span>
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section className="ops-panel ops-recientes" aria-labelledby="ops-recientes-titulo">
            <div className="ops-panel-cabecera">
              <h2 id="ops-recientes-titulo">{t('dashboardStaff.actividadReciente')}</h2>
              <Link to="/dashboard/tickets" className="ops-enlace" onClick={onLinkClick}>{t('dashboardStaff.verTodos')}</Link>
            </div>
            <div className="ops-tabla" role="table" aria-label={t('dashboardStaff.ticketsActualizadosRecientemente')}>
              <div className="ops-tabla-cabecera" role="row">
                <span role="columnheader">{t('dashboardStaff.ticket')}</span>
                <span role="columnheader">{t('dashboardStaff.departamento')}</span>
                <span role="columnheader">{t('dashboardStaff.estado')}</span>
                <span role="columnheader">{t('dashboardStaff.responsable')}</span>
                <span role="columnheader">{t('dashboardStaff.actualizado')}</span>
              </div>
              {recientes.map((ticket) => (
                <Link key={ticket.identificador} to={`/ticket/${ticket.identificador}`} className="ops-tabla-fila" role="row" onClick={onLinkClick}>
                  <span role="cell" className="ops-tabla-ticket">
                    <span className="ops-fila-asunto">{ticket.asunto}</span>
                    <span className="ops-id">#{ticket.identificador}</span>
                  </span>
                  <span role="cell">{departmentName(ticket.dpto)}</span>
                  <span role="cell"><span className={`ops-estado-ticket ops-estado-ticket--${ticket.estado_del_ticket}`}>{statusLabel(ticket.estado_del_ticket)}</span></span>
                  <span role="cell">{ticket.usuario_encargado === 'sin_asignar' ? t('dashboardStaff.sinAsignarLabel') : `@${ticket.usuario_encargado}`}</span>
                  <span role="cell"><time dateTime={ticket.updatedAt}>{formatUpdatedAt(ticket.updatedAt)}</time></span>
                </Link>
              ))}
            </div>
          </section>
        </div>

        <div className="ops-lateral">
          <section className="ops-panel" aria-labelledby="ops-hoy-titulo">
            <div className="ops-panel-cabecera">
              <h2 id="ops-hoy-titulo">{t('dashboardStaff.ops.hoyTitulo')}</h2>
            </div>
            <dl className="ops-cifras">
              <div><dt>{t('dashboardStaff.ops.entraronHoy')}</dt><dd>{stats.openedToday ?? 0}</dd></div>
              <div><dt>{t('dashboardStaff.ops.resueltosHoy')}</dt><dd>{stats.resolvedToday ?? 0}</dd></div>
              <div>
                <dt>{t('dashboardStaff.ops.tiempoMedioCierre')}</dt>
                <dd>{formatDuration(stats.avgResolutionTimeSeconds)}</dd>
              </div>
            </dl>
            {desgloseDpto.length > 0 && (
              <div className="ops-desglose">
                <button type="button" className="ops-enlace" aria-expanded={verDesglose} onClick={() => setVerDesglose((v) => !v)}>
                  {verDesglose ? t('dashboardStaff.ops.ocultarDesglose') : t('dashboardStaff.ops.verDesglose')}
                </button>
                {verDesglose && (
                  <dl className="ops-desglose-lista">
                    {desgloseDpto.map(([dpto, dato]) => (
                      <div key={dpto}>
                        <dt>{departmentName(dpto)}</dt>
                        <dd>{formatDuration(dato.avgResolutionTimeSeconds)}</dd>
                      </div>
                    ))}
                  </dl>
                )}
              </div>
            )}

            <div className="ops-antiguedad">
              <h3>{t('dashboardStaff.ops.antiguedadTitulo')}</h3>
              {abiertos.length === 0 ? (
                <p className="ops-vacio ops-vacio--corto">{t('dashboardStaff.ops.antiguedadVacia')}</p>
              ) : (
                <>
                  <div className="ops-barra" role="img" aria-label={tramos.map((tr) => `${t(`dashboardStaff.ops.${tr.clave}`)}: ${tr.n}`).join(', ')}>
                    {tramos.map((tr) => tr.n > 0 && (
                      <span key={tr.clave} className={`ops-barra-${tr.clave}`} style={{ flexGrow: tr.n }} />
                    ))}
                  </div>
                  <ul className="ops-leyenda">
                    {tramos.map((tr) => (
                      <li key={tr.clave}>
                        <i className={`ops-barra-${tr.clave}`} aria-hidden="true" />
                        <span>{t(`dashboardStaff.ops.${tr.clave}`)}</span>
                        <strong>{tr.n}</strong>
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </div>
          </section>

          <section className="ops-panel" aria-labelledby="ops-dpto-titulo">
            <div className="ops-panel-cabecera">
              <h2 id="ops-dpto-titulo">{t('dashboardStaff.ops.dptoTitulo')}</h2>
            </div>
            {porDpto.length === 0 ? (
              <p className="ops-vacio">{t('dashboardStaff.sinTickets')}</p>
            ) : (
              <ul className="ops-dptos">
                {porDpto.map(({ dpto, total, abiertos: n }) => (
                  <li key={dpto}>
                    <span className="ops-dpto-nombre">{departmentName(dpto)}</span>
                    <span className="ops-dpto-cifras">
                      {n > 0 && <em>{t('dashboardStaff.ops.dptoAbiertos', { count: n })}</em>}
                      <strong>{total}</strong>
                    </span>
                    <span className="ops-dpto-barra" aria-hidden="true"><i style={{ width: `${(total / maxDpto) * 100}%` }} /></span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      </div>

    </div>
  );
};

export default DashboardStaff;
